import { RecentlySearches } from "commonModule/services/common"
import { SoftLoginData } from "hooks/useSoftLoginDataFromLocalStorage"
import { UserBasicDetails } from "services/common"

import { getCookie, setCookie } from "./cookies"
import { canUseDOM } from "./dom"
import {
  getExpirableLocalStorage,
  removeExpirableLocalStorage,
  setExpirableLocalStorage,
} from "./expirableLocalStorage"

// ---------------------------------------------------------
const MINUTE_IN_SECONDS = 60
const HOUR_IN_SECONDS = 60 * MINUTE_IN_SECONDS
const DAY_IN_SECONDS = 24 * HOUR_IN_SECONDS
const YEAR_IN_SECONDS = 365 * DAY_IN_SECONDS
const env = process.env.NEXT_PUBLIC_IS_PRODUCTION || ""

export const setAuthToken = (value: string) =>
  setCookie(`sscAT${env}`, value, YEAR_IN_SECONDS)
export const getAuthToken = () => getCookie(`sscAT${env}`) || undefined
export const removeAuthToken = () =>
  setCookie(`sscAT${env}`, "", YEAR_IN_SECONDS)

// ---------------------------------------------------------

export const setTemporaryAuthToken = (value: string) =>
  setExpirableLocalStorage(`sscTAT${env}`, value, HOUR_IN_SECONDS)
export const getTemporaryAuthToken = () =>
  getExpirableLocalStorage(`sscTAT${env}`)
export const removeTemporaryAuthToken = () =>
  removeExpirableLocalStorage(`sscTAT${env}`)

// ---------------------------------------------------------

export const setReCaptchaToken = (value: string) =>
  setExpirableLocalStorage(`sscRCT${env}`, value, HOUR_IN_SECONDS)
export const getReCaptchaToken = () => getExpirableLocalStorage(`sscRCT${env}`)
export const removeReCaptchaToken = () =>
  removeExpirableLocalStorage(`sscRCT${env}`)

// Sticky mode: keeps the recaptcha token alive across multiple requests
// instead of consuming it after the first one. Used for the guest-booking
// flow when an existing user is detected and every booking API must keep
// sending the `whimstay-auth` header.
export const setStickyReCaptcha = () =>
  setExpirableLocalStorage(`sscRCTSticky${env}`, "1", HOUR_IN_SECONDS)
export const getStickyReCaptcha = () =>
  getExpirableLocalStorage(`sscRCTSticky${env}`)
export const removeStickyReCaptcha = () =>
  removeExpirableLocalStorage(`sscRCTSticky${env}`)

// ---------------------------------------------------------

export const setGoogleAuthToken = (value: string) =>
  setExpirableLocalStorage(`sscGAT${env}`, value, DAY_IN_SECONDS)
export const getGoogleAuthToken = () =>
  getExpirableLocalStorage(`sscGAT${env}`) || undefined
export const removeGoogleAuthToken = () =>
  removeExpirableLocalStorage(`sscGAT${env}`)

// ---------------------------------------------------------

export const setAppleAuthToken = (value: string) =>
  setExpirableLocalStorage(`sscAAT${env}`, value, DAY_IN_SECONDS)
export const getAppleAuthToken = () =>
  getExpirableLocalStorage(`sscAAT${env}`) || undefined
export const removeAppleAuthToken = () =>
  removeExpirableLocalStorage(`sscAAT${env}`)

// ---------------------------------------------------------

export const setUserCookie = (value: UserBasicDetails | undefined) =>
  setExpirableLocalStorage(
    `sscubD${env}`,
    (value && JSON.stringify(value)) || "",
    YEAR_IN_SECONDS,
  )
export const getUserCookie = () => {
  let val = getExpirableLocalStorage(`sscubD${env}`)
    ? (JSON.parse(
        getExpirableLocalStorage(`sscubD${env}`) || "",
      ) as UserBasicDetails)
    : null

  if (val) {
    return val
  }

  // if not found in local storage, check cookie to keep the session alive
  const temp = getCookie(`sscubD${env}`)
  val = temp ? (JSON.parse(temp) as UserBasicDetails) : null
  if (!val) return undefined

  // remove cookie
  setCookie(`sscubD${env}`, "", 1 / 24)

  // set expirable local storage
  setExpirableLocalStorage(
    `sscubD${env}`,
    (val && JSON.stringify(val)) || "",
    YEAR_IN_SECONDS,
  )
  return val
}
export const removeUserCookie = () => {
  removeExpirableLocalStorage(`sscubD${env}`)
}

// ------------------------Soft Login---------------------------------
// Soft login token
export const setSoftLoginAuthToken = (value: string) =>
  setCookie(`sscSLAT${env}`, value, YEAR_IN_SECONDS)
export const getSoftLoginAuthToken = () =>
  getCookie(`sscSLAT${env}`) || undefined
export const removeSoftLoginAuthToken = () =>
  setCookie(`sscSLAT${env}`, "", YEAR_IN_SECONDS)

// Soft login data

export const setSoftLoginDataInLS = (value: SoftLoginData) => {
  if (canUseDOM()) localStorage.setItem(`sscSLD${env}`, JSON.stringify(value))
}
export const getSoftLoginData = (): SoftLoginData | null => {
  if (!canUseDOM()) return null
  const data = localStorage.getItem(`sscSLD${env}`)
  return data ? (JSON.parse(data) as SoftLoginData) : null
}
export const removeSoftLoginData = () => {
  if (canUseDOM()) localStorage.removeItem(`sscSLD${env}`)
}

// ------------------------Ref TA Token (Travel Agent)-------
// ref_ta token — stored as a session cookie (no expiry = cleared when browser closes)
export const setRefTaToken = (value: string) => setCookie(`wRefTA${env}`, value)
export const getRefTaToken = () => getCookie(`wRefTA${env}`) || undefined
export const removeRefTaToken = () => setCookie(`wRefTA${env}`, "", 0)

// ref_ta bound property id — companion to the ref_ta token, same session lifetime.
// Records the single property this referral is for, so pricing shows only there.
// See shouldBindRefTaProperty in utils/refTaBinding.
export const setRefTaPropertyId = (value: string) =>
  setCookie(`wRefTAProp${env}`, value)
export const getRefTaPropertyId = () =>
  getCookie(`wRefTAProp${env}`) || undefined
export const removeRefTaPropertyId = () => setCookie(`wRefTAProp${env}`, "", 0)

// ---------------------------------------------------------

export const cleanCookiesStorage = () => {
  removeUserCookie()
  removeAuthToken()
  removeTemporaryAuthToken()
  removeSoftLoginAuthToken()
  removeSoftLoginData()
}

// ---------------------------------------------------------

export const setDeviceId = (val: string) =>
  setExpirableLocalStorage(`WsDID2${env}`, val, 5 * YEAR_IN_SECONDS)
export const getDeviceId = () => getExpirableLocalStorage(`WsDID2${env}`)

// ---------------------------------------------------------

export const getGAId = () => {
  if (!canUseDOM()) return ""

  const cookies = document.cookie
  const gaIdRegex = /_ga=GA\d\.\d\.(\d+\.\d+)/
  const matches = cookies.match(gaIdRegex)
  return matches && matches.length > 1 ? matches[1] : ""
}

// ---------------------------------------------------------

export const setFlagToShowRatingForm = (bookingNumber: string) =>
  setExpirableLocalStorage(
    "shouldShowRatingForm",
    bookingNumber,
    60 * DAY_IN_SECONDS,
  )
export const getFlagToShowRatingForm = (bookingNumber: string) =>
  getExpirableLocalStorage("shouldShowRatingForm") === bookingNumber
export const removeFlagToShowRatingForm = () =>
  removeExpirableLocalStorage("shouldShowRatingForm")

// ---------------------------------------------------------

export const setFiltersInLocalStorage = (
  value: {
    featureCategory: string
    id: number
    keyName: string
  }[],
) => {
  setExpirableLocalStorage("fco", JSON.stringify(value), DAY_IN_SECONDS)
}
export const getFiltersInLocalStorage = () =>
  getExpirableLocalStorage("fco") || JSON.stringify([])

// ---------------------------------------------------------

const detailPageOrigin = "detailPageOriginV2"
export const setDetailPageOrigin = (val: string) =>
  localStorage.setItem(detailPageOrigin, val)
export const getDetailPageOrigin = () => localStorage.getItem(detailPageOrigin)
export const removeDetailPageOrigin = () =>
  localStorage.removeItem(detailPageOrigin)

// to pass property price showing in the property card to the detail page mixpanel event.
const priceShowingInPropertyCard = "priceShowingInPropertyCard"
export const setPropertyPriceForDetailPageEvent = (val: string) =>
  localStorage.setItem(priceShowingInPropertyCard, val)
export const getPropertyPriceForDetailPageEvent = () =>
  localStorage.getItem(priceShowingInPropertyCard)
export const removePropertyPriceForDetailPageEvent = () =>
  localStorage.removeItem(priceShowingInPropertyCard)

// ----------------------------------------------------------

export const setReferralChannel = (referralChannel: string) =>
  setCookie("referralChannel", referralChannel, 1)
export const getReferralChannel = () => {
  return getCookie("referralChannel") || null
}
export const removeReferralChannel = () =>
  // localStorage.removeItem("referralChannel")
  setCookie("referralChannel", "", 1 / 24)

// -----------------------------------------------------------

// Window count
export const setWindowCount = (count: number) =>
  localStorage.setItem("windowCount", count.toString())
export const getWindowCount = () =>
  canUseDOM() ? parseInt(localStorage.getItem("windowCount") || "0") : 0

export const removeWindowCount = () => localStorage.removeItem("windowCount")

// -----------------------------------------------------------
// "Back to search" (mobile) helpers
const LAST_SEARCH_URL_KEY = "ws:lastSearchUrl"
export const setLastSearchUrl = (url: string) =>
  setCookie(LAST_SEARCH_URL_KEY, url)
export const getLastSearchUrl = () => getCookie(LAST_SEARCH_URL_KEY)

// Recently Searches Storage
const RECENTLY_SEARCHES_KEY = `recentlySearches${
  process.env.NEXT_PUBLIC_IS_PRODUCTION || ""
}`
const MAX_RECENT_SEARCHES = 5

export const getRecentlySearchesFromStorage = (): RecentlySearches[] => {
  if (!canUseDOM()) return []
  const stored = localStorage.getItem(RECENTLY_SEARCHES_KEY)
  return stored ? (JSON.parse(stored) as RecentlySearches[]) : []
}

export const addRecentlySearchToStorage = (search: RecentlySearches) => {
  if (!canUseDOM()) return
  const searches = getRecentlySearchesFromStorage()

  // Remove duplicate if exists (based on placeId)
  const filteredSearches = searches.filter(
    (item) => item.placeId !== search.placeId,
  )

  // Add new search to beginning
  filteredSearches.unshift(search)

  // Keep only the most recent MAX_RECENT_SEARCHES
  const trimmedSearches = filteredSearches.slice(0, MAX_RECENT_SEARCHES)

  localStorage.setItem(RECENTLY_SEARCHES_KEY, JSON.stringify(trimmedSearches))
}

export const clearRecentlySearchesFromStorage = () => {
  if (!canUseDOM()) return
  localStorage.removeItem(RECENTLY_SEARCHES_KEY)
}

// -----------------------------------------------------------
// Sets a Signature Stay Collection savings amount cookie that expires in ~15 minutes.
export const setWhimstaySavingsAmountCookie = (name: string, amount: number) =>
  setExpirableLocalStorage(name, amount.toString(), 15 * MINUTE_IN_SECONDS)
export const getWhimstaySavingsAmountCookie = (name: string) => {
  const value = getExpirableLocalStorage(name)
  return value ? parseFloat(value) : null
}
export const removeWhimstaySavingsAmountCookie = (name: string) => {
  removeExpirableLocalStorage(name)
}

// User country/region
const USER_COUNTRY_KEY = "country"

export const getUserCountry = () => {
  const country = getCookie(USER_COUNTRY_KEY)?.trim().toUpperCase()
  return country || "US"
}

export const getIsUserFromUs = () => {
  if (!canUseDOM()) return null

  const country = getCookie(USER_COUNTRY_KEY)?.trim().toLowerCase()
  if (!country) return null
  return country === "us" ? "true" : "false"
}

export const removeIsUserFromUs = () => {
  if (!canUseDOM()) return
  setCookie(USER_COUNTRY_KEY, "", -1)
}
